import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { compareDatesDesc } from 'global/helpers/dates'
import { deleteRequest, getChunkedResults, getRequest, postRequest, TRIP_PREFIX, IDENTITY_PREFIX, TELEMATICS_PREFIX } from 'global/helpers/requests'
import { type PaginatedResults } from 'global/types/pagination'
import i18n from 'i18n'
import { TRIP_DETAILS_QUERY_KEY } from 'screens/TripDetails/TripDetails.service'
import { type TripAcceptanceFormValues } from './TripAcceptance.helpers'
import {
  type AssignDriverFormValues,
  type AssignVehicleFormValues,
  type CountersData,
  type ShareTripViaEmailFormValues,
  type ShareTripViaSmsFormValues,
  type ShareTripViaWhatsAppFormValues,
  type SmsDriverFormValues,
  type TrackingInformation,
  type TripData,
  type TripFlag,
} from './TripDashboard.types'

// NOTE: the trip routes are served by trip-service (/api/trip/v1); the weblink sharing routes are 
// served by identity-service (/api/identity/v1)

export interface ShareTokenResponse {
  weblinkToken: string
}

export const TRIPS_QUERY_KEY = 'trips'
export const TRIPS_COUNTERS_QUERY_KEY = 'counters'
export const TRIPS_TRACKING_QUERY_KEY = 'tracking'
export const TRIP_SHARE_TOKEN_QUERY_KEY = 'trip-share-token'
  
export const getCounters = async (): Promise<CountersData> =>
  await getRequest<CountersData>('trips/filter/counters', {}, TRIP_PREFIX)

export const tripCountersQueryOptions = queryOptions({
  queryKey: [TRIPS_QUERY_KEY, TRIPS_COUNTERS_QUERY_KEY],
  queryFn: getCounters,
})

export const getPaginatedTrips = async (
  page = 0,
  pageSize = 100,
  weblinkToken?: string,
): Promise<PaginatedResults<TripData>> => {
  const trips = await getRequest<PaginatedResults<TripData>>(
    `trips?page=${page}&size=${pageSize}`,
    { token: weblinkToken },
    TRIP_PREFIX,
  )
  trips.content = trips.content.map(processTrip)
  return trips
}

export const getTrips = async (weblinkToken?: string): Promise<TripData[]> => {
  const getPaginatedTripsWrapper = (page: number, pageSize: number) => getPaginatedTrips(page, pageSize, weblinkToken)
  const trips = await getChunkedResults<TripData>(getPaginatedTripsWrapper)
  return trips.sort(sortTrips)
}

export const getPaginatedFilteredTrips = async (
  predefinedFilter: string,
  weblinkToken?: string,
  page = 0,
  pageSize = 1000,
): Promise<PaginatedResults<TripData>> => {
  const trips = await getRequest<PaginatedResults<TripData>>(
    `trips/filter/${predefinedFilter}?page=${page}&size=${pageSize}`,
    { token: weblinkToken },
    TRIP_PREFIX,
  )
  trips.content = trips.content.map(processTrip)
  return trips
}

export const getFilteredTrips = async (
  predefinedFilter: string,
  weblinkToken?: string,
): Promise<TripData[]> => {
  const getPaginatedFilteredTripsWrapper = (page: number, pageSize: number) =>
    getPaginatedFilteredTrips(predefinedFilter, weblinkToken, page, pageSize)
  const trips = await getChunkedResults<TripData>(getPaginatedFilteredTripsWrapper)
  return trips.sort(sortTrips)
}

export const tripsQueryOptions = (predefinedFilter: string | null, weblinkToken?: string) => queryOptions({
  queryKey: [TRIPS_QUERY_KEY, { predefinedFilter, weblinkToken }],
  queryFn: () => predefinedFilter ? getFilteredTrips(predefinedFilter, weblinkToken) : getTrips(weblinkToken),
  placeholderData: keepPreviousData,
})

export const getTrip = async (tripCid: string, weblinkToken?: string): Promise<TripData> =>
  processTrip(await getRequest<TripData>(`trips/${tripCid}`, { token: weblinkToken }, TRIP_PREFIX))

export const tripQueryOptions = (tripCid: string, weblinkToken?: string) => queryOptions({
  queryKey: [TRIP_DETAILS_QUERY_KEY, tripCid, { weblinkToken }],
  queryFn: () => getTrip(tripCid, weblinkToken),
})

export const deleteTrip = async (tripCid: string): Promise<void> =>
  await deleteRequest<void>(`trips/${tripCid}`, {}, TRIP_PREFIX)

export const getShareToken = async (tripCid: string, weblinkToken?: string): Promise<ShareTokenResponse> =>
  await getRequest<ShareTokenResponse>(`trips/${tripCid}/weblink`, { token: weblinkToken }, IDENTITY_PREFIX)

export const tripShareTokenQueryOptions = (tripCid: string, weblinkToken?: string) => queryOptions({
  queryKey: [TRIP_SHARE_TOKEN_QUERY_KEY, tripCid, { weblinkToken }],
  queryFn: () => getShareToken(tripCid, weblinkToken),
})

export const shareTokenViaEmail = async (
  tripCid: string,
  data: ShareTripViaEmailFormValues,
  weblinkToken?: string,
): Promise<void> =>
  await postRequest<void>(`trips/${tripCid}/weblink/mail`, { body: data, token: weblinkToken }, IDENTITY_PREFIX)

export const shareTokenViaSms = async (
  tripCid: string,
  data: ShareTripViaSmsFormValues,
  weblinkToken?: string,
): Promise<void> =>
  await postRequest<void>(`trips/${tripCid}/weblink/sms`, { body: data, token: weblinkToken }, IDENTITY_PREFIX)

export const shareTokenViaWhatsApp = async (
  tripCid: string,
  data: ShareTripViaWhatsAppFormValues,
  weblinkToken?: string,
): Promise<void> => {
  // some language codes differ between RCS and Meta
  const metaLanguageCodes: Record<string, string> = {
    'nn': 'nb',
    'pt': 'pt_PT',
  }
  return await postRequest<void>(`trips/${tripCid}/weblink/whatsapp`, {
    body: {
      ...data,
      language: metaLanguageCodes[data.language] ?? data.language,
    },
    token: weblinkToken,
  }, IDENTITY_PREFIX)
}

export const sendSmsToDriver = async (tripCid: string, data: SmsDriverFormValues): Promise<void> =>
  await postRequest<void>(`trips/${tripCid}/weblink/smsDriver`, { body: data }, IDENTITY_PREFIX)

export const assignDriver = async (tripCid: string, data: AssignDriverFormValues): Promise<void> =>
  await postRequest<void>(`assignDriver?tripCid=${tripCid}&driverCid=${data.driverCid!}`, {}, TRIP_PREFIX)

export const sendTripAcceptance = async (
  tripCid: string,
  data: TripAcceptanceFormValues,
  weblinkToken?: string,
): Promise<void> =>
  await postRequest<void>(
    `trips/${tripCid}/trip-acceptance`,
    { body: data, token: weblinkToken },
    TRIP_PREFIX,
  )

export const assignVehicle = async (tripCid: string, data: AssignVehicleFormValues): Promise<void> => {
  const processedData = {
    licensePlate: data.licensePlate,
    vehicleType: data.vehicleType,
    isTrackable: data.isTrackable,
  }
  return await postRequest<void>(`trips/${tripCid}/vehicle`, { body: processedData }, TRIP_PREFIX)
}

export const getTripTrackingInformation = async (weblinkToken?: string): Promise<TrackingInformation[]> =>
  await getRequest<TrackingInformation[]>(
    'trips/tracking-information',
    { token: weblinkToken },
    TELEMATICS_PREFIX,
  )

export const tripTrackingInformationQueryOptions = (weblinkToken?: string) => queryOptions({
  queryKey: [TRIPS_QUERY_KEY, TRIPS_TRACKING_QUERY_KEY, { weblinkToken }],
  queryFn: () => getTripTrackingInformation(weblinkToken),
})

const processTrip = (trip: TripData): TripData => {
  // NOTE: backwards compatibility
  if (!trip.voyageNumber) {
    trip.voyageNumber = trip.tripNumber
  }
  // NOTE: backwards compatibility
  if (!trip.dangerousGoodsDetails) {
    trip.dangerousGoodsDetails = []
  }
  trip.countryPickUpPlace = i18n.t(`countries.${trip.countryCodePickUpPlace}`)
  trip.countryDeliveryPlace = i18n.t(`countries.${trip.countryCodeDeliveryPlace}`)
  trip.flags?.sort(sortTripFlags)
  return trip
}

const sortTrips = (a: TripData, b: TripData): number => {
  const date = compareDatesDesc(a.creationDate, b.creationDate)
  if (date !== 0) {
    return date
  }
  return a.voyageNumber.localeCompare(b.voyageNumber)
}

const sortTripFlags = (a: TripFlag, b: TripFlag): number => {
  const flagsOrder = [
    'REFUSED',
    'SHORT',
    'DAMAGED',
    'UPDATE_STATUS',
    'POP_MISSING',
    'POD_MISSING',
    'IMAGE_MISSING',
  ]
  return flagsOrder.indexOf(a.name) - flagsOrder.indexOf(b.name)
}
